import * as React from "react";

import {
  OAuthProvider,
  TotpMultiFactorGenerator,
  getAuth,
  getMultiFactorResolver,
  getRedirectResult,
  signInWithRedirect,
} from "firebase/auth";
import {
  ReCaptchaEnterpriseProvider,
  initializeAppCheck,
} from "firebase/app-check";
import { getFunctions, httpsCallable } from "firebase/functions";
import { motion } from "framer-motion";
import styled from "styled-components";

import DownloadModal from "../components/shared/download-modal";
import Footer from "../components/sections/footer";
import Grid from "../components/shared/grid";
import Layout from "../components/shared/layout";
import Navbar from "../components/navbar";
import { SecondaryPageTitle } from "../components/shared/secondary-page-elements";
import Section from "../components/shared/section";
import Seo from "../components/shared/seo";
import Spinner from "../components/shared/Spinner";
import { initializeApp } from "firebase/app";

const MfaContainer = styled.div`
  grid-column: col-start / span 12;
  ${(props) => props.theme.device.tablet} {
    grid-column: 3 / span 8;
  }
  ${(props) => props.theme.device.laptop} {
    grid-column: 4 / span 6;
  }
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 400px;
`;

const OtpInput = styled.input`
  background-color: ${(props) => props.theme.color.uiCardGround};
  border: 1px solid ${(props) => props.theme.color.uiCardEdge};
  border-radius: 8px;
  color: ${(props) => props.theme.color.textPrimary};
  font-family: inherit;
  font-size: ${(props) => props.theme.fontSize.medium};
  font-weight: 600;
  letter-spacing: 0.15em;
  padding: 16px 20px;
  width: 100%;
  &::placeholder {
    color: ${(props) => props.theme.color.textSecondary};
    font-weight: 400;
    letter-spacing: normal;
  }
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.color.uiPillGround};
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const VerifyButton = styled(motion.button)`
  border: none;
  cursor: pointer;
  background-color: ${(props) => props.theme.color.uiPillGround};
  color: ${(props) => props.theme.color.bg};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: ${(props) => props.theme.fontSize.regular};
  font-weight: 600;
  letter-spacing: -0.01em;
  padding: 14px 28px;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.41) 0px -1px 1px -1px inset,
    rgba(0, 0, 0, 0.2) 0px 2px 4px 0px,
    rgba(0, 0, 0, 0.21) 0px -1px 0px 1px inset;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2pt ${(props) => props.theme.color.bg},
      0 0 0 5pt ${(props) => props.theme.color.textSecondary};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonSpinner = styled(Spinner)`
  & .path {
    stroke: ${(props) => props.theme.color.bg};
  }
`;

const ErrorText = styled.p`
  color: ${(props) => props.theme.color.accentRed};
  font-size: ${(props) => props.theme.fontSize.small};
  margin: 0;
`;

const SignInWithApple = () => {
  const [mfaResolver, setMfaResolver] = React.useState(null);
  const [otpInput, setOtpInput] = React.useState("");
  const [mfaError, setMfaError] = React.useState(null);
  const [isProcessingMfa, setIsProcessingMfa] = React.useState(false);
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const redirectProcessed = React.useRef(false);

  const proceedToApp = async (user) => {
    setIsRedirecting(true);
    // Get a token to log in the user in the app
    const functionResult = await httpsCallable(
      getFunctions(),
      "createCustomToken",
      null,
    )(null);
    // Check which app we need to open
    const urlParams = new URLSearchParams(window.location.search);
    const environmentParam = urlParams.get("env") || "production";
    const environment = environmentParam.match(/^[\w-]+$/)
      ? environmentParam
      : "production";
    // Open app with the token as a parameter
    let deepLink = `com.copilot.${environment}://authenticated?token=${functionResult.data}`;
    // Also send user creation date if available for validation
    const createdAt = user.metadata.createdAt;
    if (createdAt) {
      deepLink = `${deepLink}&timestamp=${createdAt}`;
    }
    window.location = deepLink;
  };

  React.useEffect(() => {
    if (redirectProcessed.current) {
      return;
    }
    redirectProcessed.current = true;

    (async () => {
      // Configure firebase depending on where this is deployed since Sign in
      // with Apple doesn't work cross-envs
      let hostname = window.location.hostname;
      let configObject;
      let reCaptchaKey;

      if (
        hostname === "copilot.money" ||
        hostname === "copilot-production-22904.firebaseapp.com"
      ) {
        configObject = {
          apiKey: "AIzaSyAMgjkeOSkHj4J4rlswOkD16N3WQOoNPpk",
          authDomain: hostname,
          databaseURL: "https://copilot-production-22904.firebaseio.com",
          projectId: "copilot-production-22904",
          storageBucket: "copilot-production-22904.appspot.com",
          messagingSenderId: "445606440735",
          appId: "1:445606440735:web:b0ac6d52da1d4c16c90a2a",
          measurementId: "G-T4HCG2XFK0",
        };
        reCaptchaKey = "6LcnFjMsAAAAAKvmQnWL981B4MbESc3i0sjrqaIs";
      } else {
        configObject = {
          apiKey: "AIzaSyBylgNCqROGMreEeefeDiCbiC6aPVtER28",
          authDomain: hostname,
          databaseURL: "https://copilot-development.firebaseio.com",
          projectId: "copilot-development",
          storageBucket: "copilot-development.appspot.com",
          messagingSenderId: "82380978046",
          appId: "1:82380978046:web:74a31939a56c4f282ba52f",
          measurementId: "G-N95RKN214S",
        };
        reCaptchaKey = "6LdeEzIsAAAAALXrixPxYfs3WMI1-QjR9qyvdwVx";
      }

      const app = initializeApp(configObject);
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(reCaptchaKey),
      });
      const auth = getAuth(app);

      try {
        // Try to log in
        const redirectResult = await getRedirectResult(auth);

        // If it fails we redirect to apple
        if (redirectResult == null) {
          signInWithRedirect(auth, new OAuthProvider("apple.com"));
        } else {
          // Otherwise this is the return redirect
          await proceedToApp(redirectResult.user);
        }
      } catch (error) {
        if (error.code === "auth/multi-factor-auth-required") {
          setMfaResolver(getMultiFactorResolver(auth, error));
        } else {
          throw error;
        }
      }
    })();
  });

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaResolver) {
      return;
    }

    const totpHint = mfaResolver.hints.find(
      (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    if (!totpHint) {
      setMfaError("No TOTP factor found.");
      return;
    }

    setIsProcessingMfa(true);
    setMfaError(null);

    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        otpInput,
      );
      const credential = await mfaResolver.resolveSignIn(assertion);
      await proceedToApp(credential.user);
    } catch (_error) {
      setMfaError("Invalid code. Please try again.");
      setIsProcessingMfa(false);
    }
  };

  const pageTitle = isRedirecting
    ? "Redirecting you to the app..."
    : mfaResolver
      ? "Two-factor authentication"
      : "Signing you in...";

  return (
    <Layout>
      <Seo title={"Sign in with Apple | Copilot"} />
      <DownloadModal />
      <Navbar />
      <Section>
        <Grid>
          <SecondaryPageTitle>{pageTitle}</SecondaryPageTitle>
          {mfaResolver && !isRedirecting && (
            <MfaContainer as="form" onSubmit={handleMfaSubmit}>
              <OtpInput
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter your 6-digit code"
                value={otpInput}
                onChange={(e) => setOtpInput(e.target.value)}
                maxLength={6}
                disabled={isProcessingMfa}
              />
              {mfaError && <ErrorText>{mfaError}</ErrorText>}
              <VerifyButton
                type="submit"
                disabled={isProcessingMfa || otpInput.length !== 6}
                whileHover={
                  isProcessingMfa || otpInput.length !== 6
                    ? {}
                    : { scale: 1.05 }
                }
              >
                {isProcessingMfa && <ButtonSpinner />}
                {isProcessingMfa ? "Verifying..." : "Verify"}
              </VerifyButton>
            </MfaContainer>
          )}
        </Grid>
      </Section>
      <Footer />
    </Layout>
  );
};

export default SignInWithApple;
